import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, Icon, PageHeader, WaitingInline } from '~/components/ui';
import { API_BASE } from '~/lib/api';
import type { CandidateInvitation, ThreadSummary } from './api/threadsClient';
import { listInvitations, listThreads } from './api/threadsClient';
import { buildInbox, inboxCounts, type InboxFilter } from './lib/threads';
import ThreadListItem from './components/ThreadListItem';
import InvitationListItem from './components/InvitationListItem';
import { useT } from '~/i18n';

const PAGE_SIZE = 20;

/** El texto de cada filtro y de cada vacío vive en `workspace.inbox`. */
const FILTERS: InboxFilter[] = ['all', 'unread', 'invitations'];

/**
 * Mensajes — el inbox del candidato.
 *
 * Un hilo ES una postulación, así que esta pantalla no inventa un modelo nuevo:
 * lista lo que devuelve `/api/threads` sin reordenar (ya viene por
 * `last_message_at` desc) y mete en la misma columna las invitaciones de
 * agencia, que para quien está buscando trabajo son lo mismo — alguien del otro
 * lado quiere hablarle. La invitación conserva su destino propio,
 * `/invitations/:matchId`, que es la pantalla a la que apunta el correo.
 */
export default function JobApplyMessagesPage() {
  const t = useT('workspace').inbox;
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [invitations, setInvitations] = useState<CandidateInvitation[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<InboxFilter>('all');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // La clave del error y no la frase: así cambia de idioma con el resto.
  const [error, setError] = useState<'loadError' | 'loadMoreError' | null>(null);
  // La lista salió, pero sin las respuestas de Gmail: el permiso se venció.
  const [gmailReconnect, setGmailReconnect] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Las invitaciones son un extra de esta pantalla: si ese endpoint se cae, el
    // inbox de correos sigue sirviendo. Por eso allSettled y no Promise.all.
    const [threadsResult, invitationsResult] = await Promise.allSettled([
      listThreads(PAGE_SIZE, 0),
      listInvitations(),
    ]);

    if (threadsResult.status === 'fulfilled') {
      setThreads(threadsResult.value.threads);
      setTotal(threadsResult.value.total);
      setGmailReconnect(threadsResult.value.gmail_error === 'gmail_reconnect_required');
    } else {
      setError('loadError');
    }
    if (invitationsResult.status === 'fulfilled') {
      setInvitations(invitationsResult.value);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = await listThreads(PAGE_SIZE, threads.length);
      setThreads((prev) => [...prev, ...page.threads]);
      setTotal(page.total);
    } catch {
      setError('loadMoreError');
    } finally {
      setLoadingMore(false);
    }
  };

  const counts = inboxCounts(threads, invitations);
  const items = buildInbox(threads, invitations, filter);
  const hasMore = filter !== 'invitations' && threads.length < total;

  return (
    <div className="space-y-6">
      <PageHeader
        scope="TBit Jobs"
        title={t.title}
        lede={t.lede}
      />

      {error && <div className="alert alert-error text-sm">{t[error]}</div>}

      {gmailReconnect && (
        <div className="alert text-sm" role="status">
          <span>{t.gmailReconnect}</span>
          <a className="btn btn-ghost btn-sm gap-2" href={`${API_BASE}/job-apply/auth/start`}>
            <Icon name="refresh" />
            {t.reconnectGmail}
          </a>
        </div>
      )}

      <div className="filters" role="group" aria-label={t.filterLabel}>
        {FILTERS.map((option) => (
          <button
            key={option}
            type="button"
            className="chip"
            aria-pressed={filter === option}
            onClick={() => setFilter(option)}
          >
            {t.filters[option]}
            <span className="chip__count">{counts[option]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <WaitingInline label={t.loading} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={filter === 'invitations' ? 'bubble' : 'inbox'}
          title={t.empty[filter].title}
          note={t.empty[filter].note}
          action={
            filter === 'all' ? (
              <Link to="/postulaciones" className="btn btn-primary btn-sm">
                {t.seeApplications}
              </Link>
            ) : filter === 'invitations' ? (
              <Link to="/perfil/bolsa-de-talento" className="btn btn-primary btn-sm">
                {t.activateTalentPool}
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <ul className="inbox">
              {items.map((item) =>
                item.kind === 'thread' ? (
                  <ThreadListItem key={item.id} thread={item.thread} />
                ) : (
                  <InvitationListItem key={item.id} invitation={item.invitation} />
                ),
              )}
            </ul>
          </div>
        </div>
      )}

      {hasMore && (
        <div className="flex justify-center">
          <button className="btn btn-ghost btn-sm" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore && <span className="loading loading-spinner loading-xs" />}
            {t.loadMore}
          </button>
        </div>
      )}
    </div>
  );
}
